import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute, except while showing pending feedback.
 * A pending add dispatches the current view only after its section is confirmed.
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;
  #pendingAdd = null;

  #handleDrawerClose = () => {
    if (this.#pendingAdd) this.#pendingAdd.dismissed = true;
  };

  async #finishPendingAdd(pending, succeeded) {
    if (this.#pendingAdd !== pending) return;
    const drawer = this.#themeDrawer;
    if (!succeeded && drawer?.isOpen) await drawer.close();
    if (this.#pendingAdd !== pending) return;
    const status = this.querySelector('[data-pogi-cart-pending]');
    const restoreFocus = status?.contains(document.activeElement) && drawer?.isOpen;
    const confirmed = this.querySelector('[data-pogi-cart-confirmed]');
    this.#pendingAdd = null;
    drawer?.removeAttribute('data-pending-add');
    if (confirmed instanceof HTMLElement) confirmed.inert = false;
    if (status instanceof HTMLElement) status.hidden = true;
    this.#dialog?.setAttribute('aria-labelledby', 'cart-drawer-heading');
    if (restoreFocus) confirmed?.querySelector('.theme-drawer__close-button')?.focus({ preventScroll: true });
    if (succeeded && drawer?.isOpen) {
      this.querySelector('cart-items-component')?.dispatchViewEvent?.();
      this.#handleDrawerOpen();
    }
  }

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.addEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.removeEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
  }

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => this.#updateStickyState());
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    if (this.#pendingAdd) {
      this.querySelector('[data-pogi-cart-pending] .theme-drawer__close-button')?.focus({ preventScroll: true });
      return;
    }
    this.#updateStickyState();

    const staleCartItems = this.querySelector(
      'cart-items-component[data-section-recovery-failed="true"]'
    );
    if (staleCartItems && typeof staleCartItems.recoverSection === 'function') {
      staleCartItems.recoverSection()
        .then(() => this.#updateStickyState())
        .catch((error) => {
          if (error?.name !== 'AbortError') {
            console.warn('[cart-drawer] Bounded section recovery failed:', error);
          }
        });
    }
    const staleDiscount = this.querySelector(
      'cart-discount-component[data-mutation-uncertain="true"], ' +
      'cart-discount-component[data-section-recovery-failed="true"]'
    );
    if (staleDiscount && typeof staleDiscount.recoverSection === 'function') {
      staleDiscount.recoverSection().catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[cart-drawer] Bounded discount recovery failed:', error);
        }
      });
    }

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {Element | null} */ (
      event.target instanceof Element
        ? event.target.closest('dialog:modal, [role="dialog"][aria-modal="true"]')
        : null
    );
    const sourceModalIsOpen = () => {
      if (!sourceModal) return false;
      if (!sourceModal.isConnected) return false;
      if (sourceModal instanceof HTMLDialogElement) return sourceModal.open;
      return Boolean(sourceModal.closest('.is-open, [aria-hidden="false"]'));
    };
    // Only PDP product-form adds opt into early presentation. Modal and other
    // producers retain their existing handoff and suppressAutoOpen semantics.
    let pending = null;
    const status = this.querySelector('[data-pogi-cart-pending]');
    const confirmed = this.querySelector('[data-pogi-cart-confirmed]');
    if (
      (shouldAutoOpen || this.#pendingAdd) && event.promise &&
      event.detail?.source === 'product-form-component' && !sourceModalIsOpen() &&
      status instanceof HTMLElement && confirmed instanceof HTMLElement
    ) {
      pending = { event, dismissed: this.#pendingAdd?.dismissed || false };
      this.#pendingAdd = pending;
      this.#themeDrawer?.setAttribute('data-pending-add', '');
      confirmed.inert = true;
      status.hidden = false;
      this.#dialog?.setAttribute('aria-labelledby', 'cart-drawer-pending-heading');
      if (shouldAutoOpen && !pending.dismissed) {
        this.#themeDrawer?.open({ returnFocusTo: event.pogiReturnFocusTo || null });
      }
    }
    const waitForSourceModalClose = (openAndSettle) => {
      if (!sourceModal) {
        openAndSettle();
        return;
      }
      if (sourceModal instanceof HTMLDialogElement) {
        sourceModal.addEventListener('close', openAndSettle, { once: true });
        return;
      }

      let finished = false;
      let observer;
      let timeout;
      const cleanup = () => {
        observer?.disconnect();
        window.clearTimeout(timeout);
        sourceModal.removeEventListener('close', finish);
      };
      const finish = () => {
        if (finished || sourceModalIsOpen()) return;
        finished = true;
        cleanup();
        openAndSettle();
      };
      observer = new MutationObserver(finish);
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['aria-hidden', 'class', 'hidden', 'open'],
        childList: true,
        subtree: true,
      });
      sourceModal.addEventListener('close', finish);
      timeout = window.setTimeout(() => {
        if (finished) return;
        finished = true;
        cleanup();
        this.dataset.modalHandoffFailed = 'true';
        window.location.assign(Theme.routes.cart_url);
      }, 10000);
      finish();
    };

    event.promise
      ?.then(async ({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if ((!shouldAutoOpen && !pending) || detail?.didError || detail?.suppressAutoOpen) {
          if (pending) await this.#finishPendingAdd(pending, false);
          settle();
          return;
        }

        const sectionRendered =
          event.pogiSectionRenderPromise ? await event.pogiSectionRenderPromise : true;
        if (!sectionRendered) {
          this.dataset.sectionRenderFailed = 'true';
          if (pending) await this.#finishPendingAdd(pending, false);
          settle();
          window.location.assign(Theme.routes.cart_url);
          return;
        }
        delete this.dataset.sectionRenderFailed;
        if (pending) {
          await this.#finishPendingAdd(pending, true);
          settle();
          return; // A shopper who dismissed the pending drawer must not be reopened.
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) {
            this.#themeDrawer?.open({
              returnFocusTo:
                !sourceModal && event.pogiReturnFocusTo instanceof HTMLElement
                  ? event.pogiReturnFocusTo
                  : null,
            });
          }
          settle();
        };

        if (sourceModalIsOpen()) {
          waitForSourceModalClose(openAndSettle);
        } else {
          openAndSettle();
        }
      })
      .catch(async (error) => {
        if (pending) await this.#finishPendingAdd(pending, false);
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
